import { useBlocker } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import type { Student, StudentForm } from '~entities/student';
import { useUpdateStudentByIdMutation } from '~entities/student';
import { Gender } from '~entities/student/modal/student';

import { useGetCurrency } from '~/shared/api/currency-api';
import { getCountryCodeFromCurrency } from '~/shared/utils/currency-country';
import { phoneInputValueToE164 } from '~/shared/utils/phone-input-e164';

import { useStudentFieldConfig } from '../hooks/use-student-field-config';

import { DateTimePicker } from '~shared/components/date-time-picker';
import {
  extractDateOnly,
  parseToNoonDate,
  toDateOnlyString,
} from '~shared/helpers/datetime';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

export const UpdateStudentForm = ({
  setIsEditing,
  data,
}: {
  isEditing: boolean;
  data: Student;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const mutation = useUpdateStudentByIdMutation();
  const { data: currencyData } = useGetCurrency();
  const defaultCountry = getCountryCodeFromCurrency(currencyData?.name);
  const { getFieldConfig } = useStudentFieldConfig();
  const tShirtSizeOptions = getFieldConfig('tShirtSize')?.options ?? [];
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const isConfirmingDiscardRef = useRef(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<StudentForm>({
    defaultValues: {
      firstName: data.firstName,
      lastName: data.lastName,
      dob: extractDateOnly(data.dob) ?? '',
      gender: data.gender === '' ? Gender.PreferNotToSay : data.gender,
      tShirtSize: data.tShirtSize,
      mobileNo: data.mobileNo,
      studentEmail: data.studentEmail,
      transportation: data.transportation,
      medical: data.medical,
      familyName: data.familyName,
      photoOptOut: data.photoOptOut ?? false,
    },
  });

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
  });

  const isNavigationBlocked = status === 'blocked';
  const isDiscardDialogOpen = showDiscardDialog || isNavigationBlocked;

  const handleDiscardDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (!isConfirmingDiscardRef.current) {
        reset?.();
      }
      isConfirmingDiscardRef.current = false;
      setShowDiscardDialog(false);
    }
  };

  const handleConfirmDiscard = () => {
    isConfirmingDiscardRef.current = true;
    if (isNavigationBlocked) {
      proceed();
    } else {
      setIsEditing(false);
    }
    setShowDiscardDialog(false);
  };

  const handleCancelEdit = () => {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const onSubmit = async (formData: StudentForm) => {
    try {
      await mutation.mutateAsync({
        studentId: data._id,
        ...formData,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className='mt-5 space-y-8'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='firstName'>First Name:</Label>
            <Input id='firstName' {...register('firstName')} />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='lastName'>Last Name:</Label>
            <Input id='lastName' {...register('lastName')} />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='dob'>DOB:</Label>
            <Controller
              name='dob'
              control={control}
              rules={{
                validate: (value) =>
                  !value ||
                  new Date(value) <= new Date() ||
                  'Birthdate cannot be in the future',
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <DateTimePicker
                    granularity='day'
                    value={parseToNoonDate(field.value) ?? undefined}
                    hourCycle={12}
                    displayFormat={{
                      hour12: 'PPP',
                    }}
                    disabledDays={(date) => date > new Date()}
                    onChange={(value) => {
                      field.onChange(toDateOnlyString(value));
                    }}
                  />
                  {error && (
                    <p className='text-[0.8rem] font-medium text-destructive'>
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='gender'>Gender:</Label>
            <Controller
              name='gender'
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger id='gender'>
                    <SelectValue placeholder='Select gender' />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(Gender).map((gender) => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='tShirt'>Costume Size</Label>
            <Controller
              name='tShirtSize'
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger id='tShirt'>
                    <SelectValue placeholder='Select size' />
                  </SelectTrigger>
                  <SelectContent>
                    {tShirtSizeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='mobile'>Mobile no</Label>
            <Controller
              name='mobileNo'
              control={control}
              render={({ field }) => (
                <PhoneInput
                  country={defaultCountry}
                  disableCountryGuess={true}
                  value={field.value || ''}
                  onChange={(value) => {
                      field.onChange(phoneInputValueToE164(value));
                    }}
                  inputStyle={{ width: '100%' }}
                  enableSearch={true}
                  searchPlaceholder='Search country...'
                  searchNotFound='No country found'
                  enableAreaCodes={true}
                  countryCodeEditable={true}
                />
              )}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              type='email'
              placeholder='Enter email'
              {...register('studentEmail')}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='transportation'>Transportation</Label>
            <Input
              id='transportation'
              placeholder='Bus, Car, Walk...'
              {...register('transportation')}
            />
          </div>
          <div className='flex items-center justify-between gap-4 rounded-md border p-3 md:col-span-2'>
            <div className='space-y-0.5'>
              <Label htmlFor='photo-opt-out'>Photo / video opt-out</Label>
              <p className='text-sm text-muted-foreground'>
                Shown to teachers on the attendance roster so this student is
                not photographed or filmed.
              </p>
            </div>
            <Controller
              control={control}
              name='photoOptOut'
              render={({ field }) => (
                <Switch
                  id='photo-opt-out'
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </div>

        <div className='space-y-4'>
          <h3 className='font-semibold'>Medical</h3>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='doctor'>Primary Doctor</Label>
              <Input
                id='doctor'
                placeholder='Dr Ross 555-555-5555'
                {...register('medical.primaryDoctor')}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='medication'>Medication</Label>
              <Input
                id='medication'
                placeholder='Enter medication'
                {...register('medical.medications')}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='disabilities'>Disabilities</Label>
              <Input
                id='disabilities'
                placeholder='Enter disabilities'
                {...register('medical.disabilities')}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='allergies'>Allergies</Label>
              <Input
                id='allergies'
                placeholder='Enter allergies'
                {...register('medical.allergies')}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='special-needs'>Special Needs</Label>
              <Input
                id='special-needs'
                placeholder='Enter special needs'
                {...register('medical.specialNeeds')}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='insurance'>Health Insurance Carrier</Label>
              <Input
                id='insurance'
                placeholder='Enter insurance carrier'
                {...register('medical.healthInsuranceCarrier')}
              />
            </div>
          </div>
        </div>
        <div className='flex flex-wrap justify-end gap-3'>
          <Button type='button' variant='outline' onClick={handleCancelEdit}>
            Cancel Edit
          </Button>
          <Button type='submit' className='bg-custom-secondary-1 text-white'>
            {mutation.isPending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Saving Changes
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
      <AlertDialog
        open={isDiscardDialogOpen}
        onOpenChange={handleDiscardDialogOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. If you leave now, your edits will be
              lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
